import React, { useState } from 'react';
import { Download, Layers } from 'lucide-react';
import { adminService } from '../../services/adminService';
import { Button, Checkbox, SegmentedControl } from '../ui';
import {
  FORMAT_ITEMS,
  InlineError,
  PanelBody,
  PanelLine,
  PanelToggle,
  ProgressLine,
  RowShell,
  transferMeta,
  useTransferLog,
} from './ImportExportRow';

export const BulkOperations: React.FC = () => {
  const [selectedEntities, setSelectedEntities] = useState<('stakeholders' | 'organizations')[]>([]);
  const [exportFormat, setExportFormat] = useState<'csv' | 'excel'>('excel');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const log = useTransferLog('bulk');

  const entityOptions = [
    { value: 'stakeholders', label: 'Stakeholders' },
    { value: 'organizations', label: 'Organizations' },
  ] as const;

  const handleEntityChange = (entity: 'stakeholders' | 'organizations', checked: boolean) => {
    if (checked) {
      setSelectedEntities([...selectedEntities, entity]);
    } else {
      setSelectedEntities(selectedEntities.filter(e => e !== entity));
    }
  };

  const handleBulkExport = async () => {
    if (selectedEntities.length === 0) {
      setError('Please select at least one entity type to export');
      return;
    }

    setExporting(true);
    setError(null);

    try {
      const blob = await adminService.bulkExport(selectedEntities, exportFormat);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulk-export-${new Date().toISOString().split('T')[0]}.${exportFormat === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      log.markExport();
    } catch (err) {
      setError('Bulk export failed. Please try again.');
      console.error('Bulk export error:', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <RowShell
      icon={Layers}
      label="Bulk export"
      meta={log.lastExport ? transferMeta(log.lastExport, null) : 'Several entities, one file'}
      expanded={open}
      actions={<PanelToggle icon={Download} label="Export" open={open} onClick={() => setOpen((v) => !v)} controls="bulk-export" />}
    >
      {open && (
        <PanelBody id="bulk-export">
          <PanelLine label="Entities">
            {entityOptions.map((option) => (
              <Checkbox
                key={option.value}
                label={option.label}
                checked={selectedEntities.includes(option.value)}
                onChange={(e) => handleEntityChange(option.value, e.target.checked)}
              />
            ))}
          </PanelLine>
          <PanelLine label="Format">
            <SegmentedControl label="Bulk export format" items={FORMAT_ITEMS} value={exportFormat} onChange={setExportFormat} />
          </PanelLine>
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="dark"
              onClick={handleBulkExport}
              disabled={selectedEntities.length === 0}
              loading={exporting}
            >
              <Download className="h-3.5 w-3.5" strokeWidth={1.75} />
              Download
            </Button>
          </div>
        </PanelBody>
      )}
      {exporting && <ProgressLine label="Exporting" />}
      {error && <InlineError>{error}</InlineError>}
    </RowShell>
  );
};
